<template>
  <div class="relative w-full z-[999999]">
    <div class="absolute top-5 left-1/2 transform -translate-x-1/2 space-y-2">
      <transition-group
        name="toast"
        tag="div"
        class="space-y-2"
      >
        <div
          v-for="toast in toastStore.messages"
          :key="toast.id"
          class="relative flex items-start justify-between w-80 p-3 border rounded-lg shadow-lg bg-opacity-80 backdrop-blur-sm"
          :class="GetClass('bg', toast.severity, 50)"
        >
          <div class="flex items-start space-x-3">
            <!-- Dynamic Icon -->
            <component
              :is="getIconComponent(toast.severity)"
              class="w-5 h-5"
              :class="[GetClass('stroke', toast.severity, 600), GetClass('fill', toast.severity, '50/80')]"
            />
            <div>
              <p
                class="font-semibold"
                :class="GetClass('text', toast.severity, 600)"
              >
                {{ toast.summary }}
              </p>
              <p class="text-gray-600 text-sm">{{ toast.detail }}</p>
            </div>
          </div>

          <!-- Close Button -->
          <button
            @click="BremoveToast(toast.id)"
            class="rounded-full p-1 duration-300"
            :class="GetClass('hover:bg', toast.severity, 200)"
          >
            <XMarkIcon class="w-5 h-5" :class="GetClass('stroke', toast.severity, 500)" />
          </button>

          <!-- Progress Bar -->
          <div
            v-if="toast.life > 0"
            class="absolute bottom-0 left-0 h-1 rounded-lg mx-1"
            :class="GetClass('bg', toast.severity, 500)"
            :style="{ width: `${toast.progress / 10}%` }"
          ></div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup>
import { useToast } from "@/plugins/toastPlugin";
import { onMounted, onUnmounted } from "vue";

const toastStore = useToast();

const BremoveToast = (id) => {
  toastStore.removeToast(id);
};

const getIconComponent = (type) => {
  switch (type) {
    case "success":
      return "CheckIcon";
    case "error":
      return "XCircleIcon";
    case "info":
      return "InformationCircleIcon";
    case "warn":
      return "ExclamationTriangleIcon";
    default:
      return "XCircleIcon";
  }
};

const GetColor = (type) => {
  switch (type) {
    case "success":
      return "green";
    case "error":
      return "red";
    case "info":
      return "blue";
    case "warn":
      return "yellow";
    default:
      return "gray";
  }
};

const GetClass = (type = "bg", severity = "info", fullness = 500) => {
  let color = GetColor(severity);
  return `${type}-${color}-${fullness}`;
};

// Update progress for each toast
const updateProgress = () => {
  toastStore.messages.forEach((toast) => {
    if (toast.life > 0) {
      const elapsed = Date.now() - toast.startTime;
      toast.progress = Math.max(0, 1000 - (elapsed / toast.life) * 1000); // Scale progress to 1000
    }
  });
};

// Start a timer to update progress
let interval;
onMounted(() => {
  interval = setInterval(updateProgress, 50); // Update more frequently for smoother animation
});

onUnmounted(() => {
  clearInterval(interval);
});
</script>
<style scoped>
/* Toast Enter Animation */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.toast-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}
.toast-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.toast-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>